import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api-client";

export type LeadSource = "meta_ads" | "website_form" | "whatrite";
export type LeadStatus =
  | "new"
  | "interested"
  | "follow_up"
  | "not_interested"
  | "converted"
  | "lost";

export interface LeadSummary {
  id: string;
  workspaceId: string;
  externalId: string;
  source: LeadSource;
  status: LeadStatus;
  name: string | null;
  email: string | null;
  phoneE164: string | null;
  company: string | null;
  city: string | null;
  state: string | null;
  assignedTo: string | null;
  assignedAt: string | null;
  nextFollowUpAt: string | null;
  sourceCampaignName: string | null;
  sourceAdName: string | null;
  sourceFormId: string | null;
  submittedAt: string;
  lastActivityAt: string | null;
  // Most recent human-logged activity (call/meeting/note/email/whatsapp),
  // denormalised onto the lead. null when the lead has no logged activity.
  lastLoggedActivityType: string | null;
  lastLoggedActivityAt: string | null;
  createdAt: string;
  deletedAt?: string | null;
  rawFields?: Record<string, unknown> | null;
  // Most recent call — present on the detail response only (not list rows).
  lastCall?: { occurredAt: string; outcome: string | null } | null;
}

interface ListResponse {
  leads: LeadSummary[];
  pagination: { limit: number; offset: number; returned: number; total?: number };
}

interface DetailResponse {
  lead: LeadSummary;
}

export interface LeadListFilters {
  status?: string;
  source?: string;
  assignedTo?: string;
  campaign?: string;
  city?: string[];
  state?: string[];
  q?: string;
  from?: string;
  to?: string;
  sortBy?: "created_at" | "follow_up" | "assigned_at";
  sortDir?: "asc" | "desc";
  followUp?: "all" | "due" | "scheduled";
  limit?: number;
  offset?: number;
}

export const leadKeys = {
  list: (workspaceSlug: string | null, filters: LeadListFilters) =>
    ["leads", workspaceSlug, filters] as const,
  detail: (id: string, workspaceSlug: string | null, includeDeleted: boolean) =>
    ["leads", workspaceSlug, "detail", id, includeDeleted] as const,
};

export function useLeadsQuery(
  workspaceSlug: string | null,
  filters: LeadListFilters = {}
) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: leadKeys.list(workspaceSlug, filters),
    queryFn: () =>
      apiClient.get<ListResponse>("/api/leads", {
        params: {
          workspace: workspaceSlug ?? undefined,
          ...(filters.status ? { status: filters.status } : {}),
          ...(filters.source ? { source: filters.source } : {}),
          ...(filters.assignedTo ? { assignedTo: filters.assignedTo } : {}),
          ...(filters.campaign ? { campaign: filters.campaign } : {}),
          ...(filters.city?.length ? { city: filters.city } : {}),
          ...(filters.state?.length ? { state: filters.state } : {}),
          ...(filters.q ? { q: filters.q } : {}),
          ...(filters.from ? { from: filters.from } : {}),
          ...(filters.to ? { to: filters.to } : {}),
          ...(filters.sortBy ? { sortBy: filters.sortBy } : {}),
          ...(filters.sortDir ? { sortDir: filters.sortDir } : {}),
          ...(filters.followUp ? { followUp: filters.followUp } : {}),
          ...(filters.limit !== undefined ? { limit: String(filters.limit) } : {}),
          ...(filters.offset !== undefined ? { offset: String(filters.offset) } : {}),
        },
      }),
  });
}

export function useLeadQuery(
  workspaceSlug: string | null,
  id: string | undefined,
  opts: { includeDeleted?: boolean } = {}
) {
  const includeDeleted = !!opts.includeDeleted;
  return useQuery({
    enabled: !!workspaceSlug && !!id,
    queryKey: leadKeys.detail(id ?? "", workspaceSlug, includeDeleted),
    queryFn: () =>
      apiClient.get<DetailResponse>(`/api/leads/${id}`, {
        params: {
          workspace: workspaceSlug ?? undefined,
          ...(includeDeleted ? { includeDeleted: "1" } : {}),
        },
      }),
  });
}

export function useLeadCampaignsQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["leads", workspaceSlug, "_campaigns"],
    queryFn: () =>
      apiClient.get<{ campaigns: string[] }>("/api/leads/_campaigns", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    staleTime: 60_000,
  });
}

export function useLeadCitiesQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["leads", workspaceSlug, "_cities"],
    queryFn: () =>
      apiClient.get<{ cities: string[] }>("/api/leads/_cities", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    // Server caches these ~40m; keep the client copy fresh for 30m to match.
    staleTime: 30 * 60_000,
  });
}

export function useLeadStatesQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["leads", workspaceSlug, "_states"],
    queryFn: () =>
      apiClient.get<{ states: string[] }>("/api/leads/_states", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    staleTime: 30 * 60_000,
  });
}

export function useDeletedLeadsQuery(workspaceSlug: string | null) {
  return useQuery({
    enabled: !!workspaceSlug,
    queryKey: ["leads", workspaceSlug, "_trash"],
    queryFn: () =>
      apiClient.get<{ leads: LeadSummary[] }>("/api/leads/_trash", {
        params: { workspace: workspaceSlug ?? undefined },
      }),
  });
}

// Direct set/clear of a lead's follow-up date. `followUpAt` is YYYY-MM-DD
// (Asia/Kolkata) to set, or null to clear.
export function useSetFollowUp(workspaceSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { leadId: string; followUpAt: string | null }) =>
      apiClient.patch<{ lead: LeadSummary }>(
        `/api/leads/${input.leadId}/follow-up`,
        {
          params: { workspace: workspaceSlug ?? undefined },
          json: { followUpAt: input.followUpAt },
        }
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ["leads", workspaceSlug] });
      void qc.invalidateQueries({ queryKey: ["activities", workspaceSlug, vars.leadId] });
      void qc.invalidateQueries({ queryKey: ["analytics"] });
    },
  });
}

export function useRestoreLead(workspaceSlug: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiClient.post<{ lead: LeadSummary }>(`/api/leads/${id}/restore`, {
        params: { workspace: workspaceSlug ?? undefined },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["leads", workspaceSlug] });
    },
  });
}
